<template>
  <NuxtLayout :name="isSharedLink ? 'shared-link' : 'centered'">
    <img
      v-if="!isSharedLink"
      :src="organization?.logoUrl ?? exfluentialLogoSrc"
      alt="Organization logo"
      class="w-40 h-40 object-contain mx-auto mt-13"
    />
    <component
      :is="componentToRender"
      v-model="email"
      @change-type="componentType = $event"
    />
  </NuxtLayout>
</template>

<script setup lang="ts">
import { definePageMeta, ref, computed, markRaw, useRoute } from '#imports';
import { SignIn } from '~/pages/auth/login/_components/sign-in';
import { EnterPassword } from '~/pages/auth/login/_components/enter-password';
import { RequestInvite } from '~/pages/auth/login/_components/request-invite';
import {
  ResendConfirmInvite,
  ResendWelcomeInvite,
} from '~/pages/auth/login/_components/resend-invite';
import { SelfRegisterType } from '~/api/auth';
import exfluentialLogoSrc from '~/assets/images/exfluential-logo.svg?url';
import { authForbidden } from '~/middleware/auth-forbidden';
import { RegisterYourself } from '~/pages/auth/login/_components/register-yourself';
import { useOrganizationStore } from '~/store/organization';
import { SharedLinkSignIn } from '~/pages/auth/login/_components/shared-link-sign-in';
import { GetAccess } from '~/pages/auth/login/_components/get-access';
import {
  GET_ACCESS_STEP,
  isSharedLinkRedirect,
  type LoginStep,
} from '~/pages/auth/login/login.constants';
import { getFirstQueryParam } from '~/utils/common';

definePageMeta({
  layout: false,
  middleware: authForbidden,
});

const { organization } = storeToRefs(useOrganizationStore());

const route = useRoute();
const isSharedLink = computed(() =>
  isSharedLinkRedirect(getFirstQueryParam(route.query.redirect)),
);

const email = ref('');
const componentType = ref<LoginStep>(SelfRegisterType.SIGN_IN);

const ComponentTypeMap: Record<LoginStep, unknown> = {
  [SelfRegisterType.SIGN_IN]: markRaw(SignIn),
  [SelfRegisterType.ENTER_PASSWORD]: markRaw(EnterPassword),
  [SelfRegisterType.REQUEST_INVITE]: markRaw(RequestInvite),
  [SelfRegisterType.RESEND_WELCOME_INVITE]: markRaw(ResendWelcomeInvite),
  [SelfRegisterType.RESEND_CONFIRMATION_INVITE]: markRaw(ResendConfirmInvite),
  [SelfRegisterType.REGISTER_YOURSELF]: markRaw(RegisterYourself),
  [GET_ACCESS_STEP]: markRaw(GetAccess),
};

const componentToRender = computed(() =>
  isSharedLink.value && componentType.value === SelfRegisterType.SIGN_IN
    ? markRaw(SharedLinkSignIn)
    : ComponentTypeMap[componentType.value],
);
</script>
